/**
 * The header of a KANON klienci v2 screen: the title, the controls flush right on its row, and the
 * statistics under them.
 *
 * Promoted out of `pages/client-roster/ui/RosterHeader.tsx` on 3 September 2026, with the
 * stylesheet beside it, on its second consumer — the plan library, whose own canon
 * (`Programy v2 - D3 (0.8)`) draws this band at 1:1 while rescaling everything below it. The
 * owner's 11 August disposition is that the two screens end their header bands at the same y, and
 * `e2e/header-parity.spec.ts` is what fails when they stop.
 *
 * ── What it owns, which is the reason it is a component and not only a stylesheet ────────────
 * Three decisions, each of which was about to be transcribed a second time:
 *
 * · **A figure that is not known yet is an em dash, never a zero.** "0 AKTYWNYCH · 0 ZAPROSZONYCH ·
 *   0 ŁĄCZNIE" beside "Nie udało się wczytać" is not a degraded header, it is three false
 *   statements — and the first one a trainer would act on. Every screen in this app draws absence
 *   the same way, and it is the same rule the tables under these headers already follow.
 * · **The dash is `aria-hidden` and a phrase is read in its place.** A listener hears what the mark
 *   MEANS rather than a punctuation character.
 * · **The statistics are a definition list.** Each figure is the value OF its label, and that is
 *   the one relationship a screen reader has no other way to hear. `dt` comes first because the
 *   grammar requires it and because the reading that falls out — "aktywnych, 7" — is the one a
 *   listener can follow; the canon's visual order is the reverse and the stylesheet gets there with
 *   `order`, not by inverting the DOM.
 *
 * ── What the CALLER owns ─────────────────────────────────────────────────────────────────────
 * Its controls, whole, as a node. The roster carries an archive square with a request badge and an
 * ember invite square; the library carries one ember square. A prop per control would have grown a
 * `ghostBadge` and an `onOpenArchive` this screen has no use for, and a header with two optional
 * button descriptions is a prop bag rather than a module. The classes those controls wear are
 * exported beside this file in `screenHeader.module.css` — `.ghost`, `.ghostIcon`, `.ghostBadge`,
 * `.primary`, `.primaryIcon` — so the geometry is still declared once.
 *
 * It renders in EVERY state, including loading and error. That is the canon's construction and it
 * is also what lets `AppShell` announce the screen: the shell moves focus to the `h1` after a route
 * change, and a screen with no heading until its data lands is a screen it silently fails to
 * announce.
 */

import type { ReactNode, Ref } from 'react';

import styles from './screenHeader.module.css';
import hidden from './visuallyHidden.module.css';

/**
 * The `+` on the band's primary square.
 *
 * It lives here rather than in either screen's own glyph file because it is the same drawing on
 * both — `M10 4.5v11M4.5 10h11` on a 20-unit box at a 2px round-capped stroke, which is what the
 * canon writes for the roster's INVITE and the library's NEW PROGRAM alike. The two mean different
 * things and each button says so in its own accessible name; the mark is one mark.
 *
 * `aria-hidden`, always: an icon-only control carries its whole name on the button.
 */
export function ScreenHeaderPlus({ className }: { readonly className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden focusable="false">
      <path d="M10 4.5v11M4.5 10h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** One statistic: its word, and the figure it is the label of. `null` while the read is open or after it failed. */
export interface ScreenHeaderStat {
  readonly label: string;
  readonly value: number | null;
}

/**
 * What an unknown figure looks like and what it is called.
 *
 * Two resolved strings rather than two keys: a `shared/` component may not know a feature's locale
 * namespace. Stated once for the whole header, because a header whose three figures explained their
 * absence in three different ways would be saying that they are absent for three different reasons.
 */
export interface ScreenHeaderAbsence {
  /** The visible mark. An em dash on both screens today. */
  readonly mark: string;
  /** What is read in its place. */
  readonly spoken: string;
}

interface ScreenHeaderProps {
  readonly title: string;
  /**
   * Where `AppShell` moves focus after a route change. It is not a control, so `tabIndex={-1}`
   * below keeps it out of the tab order while leaving it focusable.
   */
  readonly titleRef: Ref<HTMLHeadingElement>;
  /** The controls flush right on the title's row, in the canon's order. */
  readonly actions: ReactNode;
  readonly stats: readonly ScreenHeaderStat[];
  readonly absence: ScreenHeaderAbsence;
}

function Figure({ value, absence }: { readonly value: number | null; readonly absence: ScreenHeaderAbsence }) {
  if (value !== null) return <>{value}</>;
  return (
    <>
      <span aria-hidden>{absence.mark}</span>
      <span className={hidden.srOnly}>{absence.spoken}</span>
    </>
  );
}

export function ScreenHeader({ title, titleRef, actions, stats, absence }: ScreenHeaderProps) {
  return (
    <header className={styles.header}>
      <div className={styles.titleRow}>
        <h1 className={styles.title} ref={titleRef} tabIndex={-1}>
          {title}
        </h1>
        {actions}
      </div>

      <dl className={styles.stats}>
        {stats.map((stat) => (
          <div key={stat.label} className={styles.stat}>
            <dt className={styles.label}>{stat.label}</dt>
            <dd className={styles.figure}>
              <Figure value={stat.value} absence={absence} />
            </dd>
          </div>
        ))}
      </dl>
    </header>
  );
}
